import * as React from 'react';
import { GallerySidebarContainer } from 'shared/containers/GallerySidebar';

import * as styles from './Gallery.css';
import { GalleryFullscreen } from './components/GalleryFullscreen';
import { GalleryFullscreen as GalleryFullscreenRedesigned } from './components/GalleryFullscreenRedesigned';
import { GalleryInner } from './components/GalleryInner';
import { InfoControls } from './components/InfoControls';
import { useGalleryPhotoSlideTelemetry } from './hooks/useGalleryPhotoSlideTelemetry';
import { trackFullscreenPhotoSlide, trackPreviewPhotoSlide } from './tracking';
import { TMedia } from './types';

interface IGallerySlots {
  slotFullscreenBefore?: React.ComponentProps<typeof GalleryFullscreen>['slotBefore'];
  slotScrollerAfter?: React.ReactElement;
}

interface IGalleryProps extends IGallerySlots {
  isFullscreenRedesign?: boolean;
  media: TMedia[];
  onFullscreenOpen?(): void;
}

const GalleryComponent: React.FC<React.PropsWithChildren<IGalleryProps>> = ({
  isFullscreenRedesign,
  slotScrollerAfter,
  slotFullscreenBefore,
  media,
  onFullscreenOpen,
}) => {
  const galleryInnerRef = React.useRef<React.ElementRef<typeof GalleryInner>>(null);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = React.useState(false);
  const trackPhotoSlideChange = useGalleryPhotoSlideTelemetry();
  const previewSlideRef = React.useRef(0);
  const fullscreenSlideRef = React.useRef(0);

  /* istanbul ignore next */
  const handleSlideChange = React.useCallback(
    (index: number) => {
      if (index === previewSlideRef.current) {
        return;
      }

      trackPreviewPhotoSlide(previewSlideRef.current, index);
      previewSlideRef.current = index;
      trackPhotoSlideChange(index);
      setCurrentSlide(index);
    },
    [trackPhotoSlideChange],
  );

  /* istanbul ignore next */
  const handleFullscreenSlideChange = React.useCallback(
    (index: number) => {
      if (index === fullscreenSlideRef.current) {
        return;
      }

      trackFullscreenPhotoSlide(fullscreenSlideRef.current, index);
      fullscreenSlideRef.current = index;
      previewSlideRef.current = index;
      trackPhotoSlideChange(index);
      galleryInnerRef.current?.scrollTo(index, { behavior: 'auto' });
      setCurrentSlide(index);
    },
    [trackPhotoSlideChange],
  );

  const handleScrollerClick = React.useCallback(() => {
    fullscreenSlideRef.current = previewSlideRef.current;
    setIsFullscreenOpen(true);

    if (onFullscreenOpen) {
      onFullscreenOpen();
    }
  }, [onFullscreenOpen]);

  const handleFullscreenClose = React.useCallback(() => {
    setIsFullscreenOpen(false);
  }, []);

  if (media.length === 0) {
    return <div className={styles['no-media']} />;
  }

  return (
    <div className={styles['container']}>
      <GalleryInner
        ref={galleryInnerRef}
        bordered
        fullscreenable
        keyboard={!isFullscreenOpen}
        media={media}
        objectFit="contain"
        withBackgroundBlur
        slotScrollerEnd={
          <>
            {slotScrollerAfter}
            <InfoControls media={media} />
          </>
        }
        onSlideChange={handleSlideChange}
        onScrollerClick={handleScrollerClick}
        currentSlide={currentSlide}
      />
      {!isFullscreenRedesign ? (
        <GalleryFullscreen
          slotBefore={slotFullscreenBefore}
          media={media}
          initialSlide={currentSlide}
          open={isFullscreenOpen}
          onSlideChange={handleFullscreenSlideChange}
          onClose={handleFullscreenClose}
          currentSlide={currentSlide}
        />
      ) : (
        <GalleryFullscreenRedesigned
          currentSlide={currentSlide}
          media={media}
          initialSlide={currentSlide}
          open={isFullscreenOpen}
          onSlideChange={handleFullscreenSlideChange}
          onClose={handleFullscreenClose}
          slotSidebar={<GallerySidebarContainer />}
        />
      )}
    </div>
  );
};

export const Gallery = React.memo(GalleryComponent);
